/**
 * MedCaddy API Client - Core Infrastructure
 * Handles base configuration, CSRF, session state, token refresh, and base fetch wrapper.
 */

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
export const API_BASE_URL = API_URL.replace(/\/api\/?$/, '');

/** Purpose: Builds legal-doc endpoint URL for configured API origin. Inputs: slug. Output: legal doc URL string. */
export function getLegalDocUrl(slug) {
  return `${API_BASE_URL}/api/legal/docs/${slug}`;
}

let csrfToken = null;
const AUTH_STATE_KEY = 'medcaddy_has_session';
let hasSession = false;
let refreshPromise = null;
let refreshFailedAt = 0;
const REFRESH_COOLDOWN_MS = 30000;

if (typeof window !== 'undefined') {
  hasSession = window.localStorage.getItem(AUTH_STATE_KEY) === '1';
}

/** Purpose: Updates has session. Inputs: value. Output: value. */
export function setHasSession(value) {
  hasSession = value;
  if (typeof window !== 'undefined') {
    if (value) {
      window.localStorage.setItem(AUTH_STATE_KEY, '1');
    } else {
      window.localStorage.removeItem(AUTH_STATE_KEY);
    }
  }
}

/** Purpose: Fetches csrf token. Inputs: none. Output: csrfToken. */
export function getCsrfToken() {
  return csrfToken;
}

/** Purpose: Sets csrf token. Inputs: token. Output: none. */
export function setCsrfToken(token) {
  csrfToken = token;
}

/** Purpose: Executes clear auth state. Inputs: none. Output: Operation result. */
export function clearAuthState() {
  csrfToken = null;
  setHasSession(false);
}

/** Purpose: Fetches has session. Inputs: none. Output: Operation result. */
export function getHasSession() {
  return hasSession;
}

/** Purpose: Executes fetch csrf token. Inputs: fetchCsrfToken. Output: Promise with operation result. */
export async function fetchCsrfToken() {
  const response = await fetch(`${API_URL}/auth/csrf`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch CSRF token');
  }

  const data = await response.json();
  csrfToken = data.csrfToken;
  return csrfToken;
}

/** Purpose: Executes should attach csrf. Inputs: method. Output: method. */
function shouldAttachCsrf(method) {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method);
}

export async function ensureCsrf(headers) {
  if (!csrfToken) {
    try {
      await fetchCsrfToken();
    } catch (error) {
      console.error('CSRF token fetch failed:', error);
    }
  }
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
}

/** Purpose: Checks whether a response is a CSRF token mismatch error. Inputs: response. Output: Promise<boolean>. */
async function isCsrfInvalidResponse(response) {
  if (response.status !== 403) return false;
  try {
    const data = await response.clone().json();
    return data?.error === 'CSRF_INVALID';
  } catch (_error) {
    return false;
  }
}

export function assertOk(response, errorMessage) {
  if (response.status === 401) throw new Error('UNAUTHORIZED');
  if (!response.ok) throw new Error(errorMessage);
}

export async function assertOkWithDetail(response, defaultMessage) {
  if (response.status === 401) throw new Error('UNAUTHORIZED');
  if (!response.ok) {
    let error;
    try {
      error = await response.json();
    } catch {
      throw new Error(defaultMessage);
    }
    if (error.code === 'EMAIL_NOT_VERIFIED') {
      throw new Error('Please verify your email address before performing this action.');
    }
    throw new Error(error.message || error.error || defaultMessage);
  }
}

// Base fetch wrapper with auth + CSRF
export async function apiFetch(endpoint, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    ...options.headers
  };

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (shouldAttachCsrf(method)) {
    await ensureCsrf(headers);
  }

  let response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });

  // Handle stale CSRF token by refreshing and retrying once.
  if (shouldAttachCsrf(method) && await isCsrfInvalidResponse(response)) {
    try {
      await fetchCsrfToken();
    } catch (error) {
      console.error('CSRF token refresh failed after 403:', error);
    }

    const retryHeaders = { ...headers };
    if (csrfToken) {
      retryHeaders['X-CSRF-Token'] = csrfToken;
    } else {
      delete retryHeaders['X-CSRF-Token'];
    }

    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: retryHeaders,
      credentials: 'include'
    });
  }

  // Handle token expiration
  if (
    response.status === 401 &&
    hasSession &&
    endpoint !== '/auth/login' &&
    endpoint !== '/auth/register' &&
    endpoint !== '/auth/refresh'
  ) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const retryHeaders = { ...headers };
      if (shouldAttachCsrf(method)) {
        if (!csrfToken) {
          await fetchCsrfToken();
        }
        if (csrfToken) {
          retryHeaders['X-CSRF-Token'] = csrfToken;
        }
      }
      return fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: retryHeaders,
        credentials: 'include'
      });
    }
  }

  return response;
}

/** Purpose: Executes try refresh token. Inputs: tryRefreshToken. Output: Promise with operation result. */
async function tryRefreshToken() {
  if (refreshPromise) return refreshPromise;
  if (Date.now() - refreshFailedAt < REFRESH_COOLDOWN_MS) {
    return false;
  }

  refreshPromise = (async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: '{}'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.csrfToken) {
          csrfToken = data.csrfToken;
        }
        setHasSession(true);
        return true;
      }

      refreshFailedAt = Date.now();
    } catch (error) {
      refreshFailedAt = Date.now();
      console.error('Token refresh failed:', error);
    } finally {
      refreshPromise = null;
    }

    clearAuthState();
    return false;
  })();

  return refreshPromise;
}
